import React from 'react';
import { 
  Settings, 
  Save, 
  Eye, 
  Bell, 
  Award, 
  Users, 
  FileText 
} from 'lucide-react';

const ScorecardSettings = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Scorecard Settings</h2>
        <p className="text-sm text-gray-500 mt-1">Configure your scorecard preferences</p>
      </div>
      
      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Display Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Eye className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">Display Settings</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default View
              </label>
              <select className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Overview</option>
                <option>Analytics</option>
                <option>Region Map</option>
                <option>Reports</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Period
              </label>
              <select className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Today</option>
                <option>This Week</option>
                <option>This Month</option>
                <option>This Quarter</option>
                <option>This Year</option>
              </select>
            </div>
            
            <div className="flex items-center">
              <input
                id="show-trends"
                name="show-trends"
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="show-trends" className="ml-3 block text-sm text-gray-700">
                Show trends compared to previous period
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="show-targets"
                name="show-targets"
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="show-targets" className="ml-3 block text-sm text-gray-700">
                Show targets on charts
              </label>
            </div>
          </div>
        </div>
        
        {/* Notification Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">Notification Settings</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="notify-reports"
                  name="notify-reports"
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="notify-reports" className="ml-3 block text-sm text-gray-700">
                  New reports available
                </label>
              </div>
              <select className="border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Email</option>
                <option>Push</option>
                <option>Both</option>
              </select>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="notify-performance"
                  name="notify-performance"
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="notify-performance" className="ml-3 block text-sm text-gray-700">
                  Performance alerts
                </label>
              </div>
              <select className="border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Email</option>
                <option>Push</option>
                <option>Both</option>
              </select>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="notify-rankings"
                  name="notify-rankings"
                  type="checkbox"
                  defaultChecked={true}
                  className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="notify-rankings" className="ml-3 block text-sm text-gray-700">
                  Ranking changes
                </label>
              </div>
              <select className="border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Email</option>
                <option>Push</option>
                <option>Both</option>
              </select>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="notify-weekly"
                  name="notify-weekly"
                  type="checkbox"
                  defaultChecked={false}
                  className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="notify-weekly" className="ml-3 block text-sm text-gray-700">
                  Weekly digest
                </label>
              </div>
              <select className="border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Email</option>
                <option>Push</option>
                <option>Both</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Score Weights */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Award className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">Score Weights</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Sales Performance
                </label>
                <span className="text-sm text-gray-500">30%</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                defaultValue="30"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Google Reviews
                </label>
                <span className="text-sm text-gray-500">20%</span>
              </div>
              <input
                type="range"
                min="0"
                max="50"
                defaultValue="20"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Maintenance
                </label>
                <span className="text-sm text-gray-500">10%</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                defaultValue="10"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Audit Score
                </label>
                <span className="text-sm text-gray-500">10%</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                defaultValue="10"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Speed of Service
                </label>
                <span className="text-sm text-gray-500">15%</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                defaultValue="15"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <div className="flex justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Quality Control
                </label>
                <span className="text-sm text-gray-500">15%</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                defaultValue="15"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
        
        {/* Access Control */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">Access Control</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store Manager Access
              </label>
              <select className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Own Store Only</option>
                <option>Region View</option>
                <option>Full Access</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regional Manager Access
              </label>
              <select className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
                <option>Own Region Only</option>
                <option>All Regions - Limited</option>
                <option>Full Access</option>
              </select>
            </div>
            
            <div className="flex items-center">
              <input
                id="export-sensitive"
                name="export-sensitive"
                type="checkbox"
                defaultChecked={false}
                className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="export-sensitive" className="ml-3 block text-sm text-gray-700">
                Allow exporting sensitive data
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="allow-sharing"
                name="allow-sharing"
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="allow-sharing" className="ml-3 block text-sm text-gray-700">
                Allow report sharing
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <button className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
          <Save className="h-4 w-4 mr-2" />
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default ScorecardSettings; 